import { useState, useEffect } from 'react';
import { Socket } from 'socket.io-client';
import { toast } from 'sonner';

export const useSessionTimer = (socket: Socket | null, isMeTheExpert: boolean, isDoctorCategory: boolean) => {
  const [timeLeft, setTimeLeft] = useState<number | null>(null);
  const [endReasonText, setEndReasonText] = useState<string | null>(null);

  // UI Flags based on category
  const isCountUp = isDoctorCategory && isMeTheExpert;
  const isTimerHidden = isDoctorCategory && !isMeTheExpert;

  // 1. Local Tick (Up or Down)
  useEffect(() => {
    if (endReasonText) return; 
    const interval = setInterval(() => {
      setTimeLeft(prev => {
        if (prev === null) return null;
        if (isCountUp) return prev + 1; // ⏱️ Expert Doctor: Count UP
        return prev > 0 ? prev - 1 : 0; // ⏱️ Lawyer/Astrology: Count DOWN
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [endReasonText, isCountUp]);

  // 2. Listen to Backend Sync
  useEffect(() => {
    if (!socket) return;

    const handleTimerUpdate = (data: { remainingSeconds: number, elapsedSeconds: number }) => {
      setTimeLeft(prev => {
        // Force the target value based on the backend's math
        const targetValue = isCountUp ? data.elapsedSeconds : data.remainingSeconds;
        
        if (prev === null || Math.abs(prev - targetValue) > 2) {
            return targetValue;
        }
        return prev;
      });
    };

    const handleSessionEnded = (data: { reason: string }) => {
      let finalMessage = "Session Ended";
      if (data.reason === 'user_ended') finalMessage = isMeTheExpert ? "Client ended the session." : "You ended the session.";
      else if (data.reason === 'expert_ended') finalMessage = isMeTheExpert ? "You ended the session." : "Expert ended the session.";
      else if (data.reason === 'user_left') finalMessage = "Client disconnected (Timeout).";
      else if (data.reason === 'expert_left') finalMessage = "Expert disconnected (Timeout).";
      else if (data.reason === 'balance_exhausted') finalMessage = "Wallet balance exhausted.";
      else if (data.reason === 'already_completed') finalMessage = "This consultation has concluded.";
      else if (data.reason === 'expert_rejected') finalMessage = "The expert is currently unavailable to take this request.";
      else if (data.reason === 'appointment_completed') finalMessage = "The appointment time has concluded."; 

      setEndReasonText(finalMessage);
      if (data.reason !== 'already_completed') toast.info(finalMessage);
    };

    socket.on('timer_update', handleTimerUpdate);
    socket.on('session_ended', handleSessionEnded);

    return () => {
      socket.off('timer_update', handleTimerUpdate);
      socket.off('session_ended', handleSessionEnded);
    };
  }, [socket, isMeTheExpert, isCountUp]);

  const formattedTime = timeLeft !== null 
    ? `${Math.floor(timeLeft / 60).toString().padStart(2, '0')}:${(timeLeft % 60).toString().padStart(2, '0')}`
    : null;

  return { 
    timeLeft, 
    setTimeLeft, 
    formattedTime, 
    endReasonText, 
    setEndReasonText,
    isTimerHidden 
  };
};